import { Link, useNavigate, useParams } from "react-router-dom";

import { useDraftBatch } from "@/api/review";
import { ErrorBanner } from "@/components/ErrorBanner";
import { ReviewPanel } from "@/components/review/ReviewPanel";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * `/jobs/:id/review` — the standalone HITL review panel for a single
 * job. Thin wrapper around `<ReviewPanel>`: fetches the batch, handles
 * loading/error states, and navigates back to the JobDetail page when
 * the job's rollup transitions it to a terminal state. The cross-job
 * `/review` queue page renders the same `<ReviewPanel>` component for
 * each pending batch (without the navigate-on-finalise behaviour).
 */
export function ReviewBatch() {
  const { id: jobId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const query = useDraftBatch(jobId);

  if (query.isPending) {
    return <Skeleton className="h-64 w-full" />;
  }
  if (query.isError) {
    return <ErrorBanner error={query.error} onRetry={() => void query.refetch()} />;
  }

  return (
    <div className="space-y-6">
      <div>
        <Link
          to={`/jobs/${jobId}`}
          className="text-xs font-mono uppercase tracking-label text-text-muted hover:text-text"
        >
          ← Back to job
        </Link>
      </div>

      <ReviewPanel
        batch={query.data}
        onAfterSubmit={(result) => {
          if (result.jobStatus !== "pending_review") {
            navigate(`/jobs/${jobId}`);
          }
        }}
      />
    </div>
  );
}
